import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MenuItemContentTemplateRef } from '../../types/menu-item-content-template-ref';
import { Nillable } from '@web/utils';
import { MenuItemMoreContentComponent } from '../menu-item-more-content/menu-item-more-content.component';
import { MenuItem } from '../../models/menu-item';

@Component({
  selector: 'app-menu-item-more',
  imports: [forwardRef(() => MenuItemMoreContentComponent)],
  templateUrl: './menu-item-more.component.html',
  styleUrls: ['./menu-item-more.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          height: 'auto',
          opacity: 1,
          overflow: 'hidden',
        }),
      ),
      state(
        'close',
        style({
          height: 0,
          opacity: 0,
          overflow: 'hidden',
        }),
      ),
      transition('open => close', [animate('100ms')]),
      transition('close => open', [animate('100ms')]),
    ]),
  ],
})
export class MenuItemMoreComponent implements OnInit {
  private readonly router = inject(Router);

  @Input({ required: true }) public item!: MenuItem;
  @Input() public contentTemplateRef: Nillable<MenuItemContentTemplateRef>;

  @Output() public readonly toggleExpansionEvent = new EventEmitter<boolean>();

  @HostBinding('class.expanded')
  public expanded = false;

  public ngOnInit(): void {
    this.expanded = this.isActiveRouterLink(this.item);
    this.notifyExpandedEvent();
  }

  public handleMoreClick(): void {
    this.expanded = !this.expanded;
    this.notifyExpandedEvent();
  }

  public isActive(): boolean {
    return this.isActiveRouterLink(this.item);
  }

  private isActiveRouterLink(item: MenuItem): boolean {
    if (item.isMore()) {
      return item.children!.some((child) => this.isActiveRouterLink(child));
    }

    if (item.isRouterLink()) {
      return this.router.isActive(this.router.createUrlTree(item.routerLink!), {
        paths: 'exact',
        queryParams: 'exact',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    }

    return false;
  }

  private notifyExpandedEvent(): void {
    this.toggleExpansionEvent.emit(this.expanded);
  }
}
